import { type Locale } from "@/shared/utils";
import Button from "../Button";
import type { RangeValue } from "./useCalendar";
import { CALENDAR_LOCALE } from "./locale";

interface Props {
  year: number;
  value: RangeValue;
  onSelectMonth: (month: number, year: number) => void;
  onSelectQuarter: (quarter: number, year: number) => void;
  onFreeRange: () => void;
  locale: Locale;
}

function getActiveMonth(value: RangeValue, year: number): number | null {
  if (!value.start || !value.end) return null;
  if (value.start.getFullYear() !== year || value.end.getFullYear() !== year) return null;
  const m = value.start.getMonth();
  const firstDay = value.start.getDate() === 1;
  const lastDay = value.end.getDate() === new Date(year, m + 1, 0).getDate();
  const sameMonth = value.end.getMonth() === m;
  return firstDay && lastDay && sameMonth ? m : null;
}

function getActiveQuarter(value: RangeValue, year: number): number | null {
  if (!value.start || !value.end) return null;
  if (value.start.getFullYear() !== year || value.end.getFullYear() !== year) return null;
  for (let q = 1; q <= 4; q++) {
    const startMonth = (q - 1) * 3;
    if (
      value.start.getMonth() === startMonth &&
      value.start.getDate() === 1 &&
      value.end.getMonth() === startMonth + 2 &&
      value.end.getDate() === new Date(year, startMonth + 3, 0).getDate()
    ) {
      return q;
    }
  }
  return null;
}

export default function CalendarPeriods({ year, value, onSelectMonth, onSelectQuarter, onFreeRange, locale }: Props) {
  const t = CALENDAR_LOCALE[locale];
  const months = Array.from({ length: 12 }, (_, i) => i);
  const activeMonth = getActiveMonth(value, year);
  const activeQuarter = getActiveQuarter(value, year);

  return (
    <div className="flex flex-col">
      <div className="flex">
        <div className="grid grid-cols-3 gap-2">
          {months.map((month) => (
            <button
              type="button"
              key={month}
              onClick={(e) => {
                e.stopPropagation();
                onSelectMonth(month, year);
              }}
              className={`
                w-20 h-9 flex items-center justify-center radius-xs text-body-regular-sm
                text-grey-950 dark:text-grey-200 cursor-pointer
                ${month === activeMonth ? "bg-brand-500 text-white" : "hover:bg-black/5"}
              `}>
              {t.monthsShort[month]}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-2 border-l surface-base-stroke pl-2 ml-2">
          {[1, 2, 3, 4].map((quarter) => (
            <button
              type="button"
              key={quarter}
              onClick={(e) => {
                e.stopPropagation();
                onSelectQuarter(quarter, year);
              }}
              className={`
                h-9 px-3 flex items-center justify-center radius-xs text-body-regular-sm
                text-grey-950 dark:text-grey-200 cursor-pointer whitespace-nowrap
                ${quarter === activeQuarter ? "bg-brand-500 text-white" : "hover:bg-black/5"}
              `}>
              {t.quarters[quarter - 1]}
            </button>
          ))}
        </div>
      </div>

      <div className="border-t surface-base-stroke mt-3 pt-3">
        <Button
          type="button"
          variant="secondary"
          size="md"
          className="w-full"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onFreeRange();
          }}>
          {t.freeRange}
        </Button>
      </div>
    </div>
  );
}
